import salarySchema from '@/engine/schema/budget/income/1-salary.json';
import benefitsSchema from '@/engine/schema/budget/income/2-benefits.json';
import pensionSchema from '@/engine/schema/budget/income/3-pension.json';
import otherSourcesSchema from '@/engine/schema/budget/income/4-other-sources.json';
import propertiesSchema from '@/engine/schema/budget/asset/1-properties.json';
import otherAssetsSchema from '@/engine/schema/budget/asset/2-other-assets.json';
import householdSchema from '@/engine/schema/budget/fixedExpense/1-household.json';
import utilitiesSchema from '@/engine/schema/budget/fixedExpense/2-utilities.json';
import careSchema from '@/engine/schema/budget/fixedExpense/3-care.json';
import transportSchema from '@/engine/schema/budget/fixedExpense/4-transport.json';
import schoolSchema from '@/engine/schema/budget/fixedExpense/5-school.json';
import pensionInsuranceSchema from '@/engine/schema/budget/fixedExpense/6-pension-insurance.json';
import professionalsSchema from '@/engine/schema/budget/fixedExpense/7-professionals.json';
import otherSourcesFixedSchema from '@/engine/schema/budget/fixedExpense/8-other-sources.json';
import leisureSchema from '@/engine/schema/budget/flexibleExpense/1-leisure.json';
import housekeepingSchema from '@/engine/schema/budget/flexibleExpense/2-housekeeping.json';
import personalCostsSchema from '@/engine/schema/budget/flexibleExpense/3-personal-costs.json';
import savingsSchema from '@/engine/schema/budget/flexibleExpense/4-savings.json';
import debtsAdministrationSchema from '@/engine/schema/budget/debts/1-debts-administration.json';
import debtsSchema from '@/engine/schema/budget/debts/2-debts.json';
import accomodationType from '@/engine/schema/income-onboarding/1-accomodation-type.json';
import currentEmploymentStatus from '@/engine/schema/income-onboarding/2-current-employment-status.json';
import budgetWithPartner from '@/engine/schema/income-onboarding/3-budget-with-partner.json';
import detailsForDependents from '@/engine/schema/income-onboarding/4-details-for-dependents.json';
import vehicleDetails from '@/engine/schema/income-onboarding/5-vehicle-details.json';
import partnerEmploymentStatus from '@/engine/schema/income-onboarding/6-current-partner-employeement-status.json';
import Screen from '@/engine/renderer/Screen';

type ScreenSchema = Parameters<typeof Screen>[0]['schema'];

// Income onboarding schemas array (5 steps: 0-4)
// Note: partnerEmploymentStatus is NOT included here - it's only used for profile warning dialogs
export const incomeOnboardingSchemas: ScreenSchema[] = [
  accomodationType as unknown as ScreenSchema,
  currentEmploymentStatus as unknown as ScreenSchema,
  budgetWithPartner as unknown as ScreenSchema,
  detailsForDependents as unknown as ScreenSchema,
  vehicleDetails as unknown as ScreenSchema,
];

// Partner employment schema kept separate for profile warning dialogs only
export const partnerEmploymentSchema = partnerEmploymentStatus as unknown as ScreenSchema;

export const PROFILE_DIALOG_STEP_MAP: Record<string, number> = {
  'accommodation-type': 0,
  'employment-status': 1,
  'budget-with-partner': 2,
  'dependents-details': 3,
  'vehicle-details': 4,
  'partner-employment-status': 5,
};

export type ProfileDialogKey = keyof typeof PROFILE_DIALOG_STEP_MAP;

export const PROFILE_DIALOG_TITLES: Record<ProfileDialogKey, string> = {
  'accommodation-type': 'Edit accommodation type',
  'employment-status': 'Edit employment status',
  'budget-with-partner': 'Edit partner details',
  'dependents-details': 'Edit dependents details',
  'vehicle-details': 'Edit vehicle details',
  'partner-employment-status': 'Edit partner employment status',
};

export const PROFILE_DIALOG_SUCCESS_MESSAGES: Record<ProfileDialogKey, string> = {
  'accommodation-type': 'Accommodation type updated',
  'employment-status': 'Employment status updated',
  'budget-with-partner': 'Partner details updated',
  'dependents-details': 'Dependents details updated',
  'vehicle-details': 'Vehicle details updated',
  'partner-employment-status': 'Partner employment status updated',
};

// Step offset: Main onboarding has max 11 steps (0-10), so income onboarding starts from 11
// Income onboarding: step 0 => 100, 1 => 101, ...; values <=100 treated as step 0
export const INCOME_ONBOARDING_STEP_OFFSET = 100;

export const MANUAL_COMPANY_OPTION_VALUE = '__manual__';
export const MANUAL_COMPANY_LABEL = 'I can\'t find my company';

export const HIDDEN_COMPANY_NAMES = [
  'Barclays Money Management',
  'Yorkshire Water Community Trust',
  'Vulnerability Registration Service',
  'PayPlan',
  'National Debtline',
  // Newly requested hides
  'Citizens Advice Mid Lincolnshire',
  'Citizens Advice Plymouth',
  'Cadent',
  'Cadent Gas',
  'Cadent Gas Network',
  'Cadent Gas Network 123',
  'Cadent Gas Network 456',
  'ENW',
];

export const BLOCKED_REFERENCE_COMPANY_KEYS = [
  'CAB',
  'Reach Out',
  'Cadent Gas',
  'Cadent Gas Network',
  'Cadent Gas Network 123',
  'Cadent Gas Network 456'
];

// Inner routing configuration based on outer selection
export const innerRoutesConfig = {
  income: [
    { key: 'salary', label: 'Salary', schema: salarySchema },
    { key: 'benefits', label: 'Benefits', schema: benefitsSchema },
    { key: 'pension', label: 'Pension', schema: pensionSchema },
    { key: 'other-sources', label: 'Other sources', schema: otherSourcesSchema },
  ],
  assets: [
    { key: 'properties', label: 'Properties', schema: propertiesSchema },
    { key: 'otherAssets', label: 'Other assets', schema: otherAssetsSchema },
  ],
  'fixed-expenses': [
    { key: 'household', label: 'Household', schema: householdSchema },
    { key: 'utilities', label: 'Utilities', schema: utilitiesSchema },
    { key: 'care', label: 'Care', schema: careSchema },
    { key: 'transport', label: 'Transport', schema: transportSchema },
    { key: 'school', label: 'School', schema: schoolSchema },
    { key: 'pension-insurance', label: 'Pension & insurance', schema: pensionInsuranceSchema },
    { key: 'professionals', label: 'Professionals', schema: professionalsSchema },
    { key: 'other-sources', label: 'Other sources', schema: otherSourcesFixedSchema },
  ],
  'flexible-expenses': [
    { key: 'leisure', label: 'Leisure', schema: leisureSchema },
    { key: 'housekeeping', label: 'Housekeeping', schema: housekeepingSchema },
    { key: 'personal-costs', label: 'Personal cost', schema: personalCostsSchema },
    { key: 'savings', label: 'Saving', schema: savingsSchema },
  ],
  debts: [
    { key: 'debts-administration', label: 'Debts administration', schema: debtsAdministrationSchema },
    { key: 'debts', label: 'Debts', schema: debtsSchema },
  ],
};

// Outer routing configuration
export const outerRoutes = [
  { key: 'income', label: 'Income' },
  { key: 'assets', label: 'Assets' },
  { key: 'fixed-expenses', label: 'Fixed expenses' },
  { key: 'flexible-expenses', label: 'Flexible expenses' },
  { key: 'debts', label: 'Debts' },
];
